/**
 * 끝을 남기지 못한 시도 닫기 (TASK-2026-004 SPEC 「포함 범위」 6, AC-02·03).
 *
 * ## 왜 필요한가
 *
 * 게임에서 설문으로 갈 때는 페이지가 통째로 바뀐다. 그 이동이 정상 클릭이면 게임이
 * 시도를 닫고 가지만, 브라우저를 닫았다가 나중에 `/collect.html`을 다시 열거나 주소를
 * 직접 열면 시작만 있는 시도가 남는다. 서버는 끝이 없는 시도가 있으면 완료를 확정하지
 * 않는다(`attempt_unfinished`). 그러면 조기 종료한 참가자의 의견이 영영 확정되지 않는다.
 *
 * 그래서 설문 화면도 checkpoint를 보고 남은 시도를 닫을 수 있어야 한다. 사유는
 * `interrupted`다 —— 살아 있는 화면이 낸 종료와 구별되어야 하고, 실제로 끝이 제때
 * 기록되지 못한 경우이기 때문이다(중앙 명세 F1-05의 end reason).
 *
 * 게임이 쓰는 복구 경로(`GameScreen`)와 같은 판정을 쓴다. 로그에 이미 종료가 있으면
 * 다시 닫지 않는다.
 */

import { finish, hasEndedAttempt, type AttemptState, type PendingEvent } from '../app/attempt';
import type { TelemetryEvent, TelemetryEventName, TelemetryPayload } from '../telemetry/events';

export interface DanglingDeps {
  /** 진행 중이라고 적혀 있는 시도. */
  read(): AttemptState | undefined;
  /** 닫은 시도를 checkpoint에 반영한다(다음 실행이 다시 닫지 않게). */
  save(state: AttemptState): boolean;
  readAll(): readonly TelemetryEvent[];
  record(name: TelemetryEventName, payload?: TelemetryPayload): unknown;
  now(): number;
}

export interface DanglingOutcome {
  /** 닫은 시도 ID. 닫을 것이 없었으면 없다. */
  readonly closed: string | undefined;
  readonly events: readonly PendingEvent['name'][];
}

export function closeDanglingAttempt(deps: DanglingDeps): DanglingOutcome {
  const pending = deps.read();
  if (pending === undefined) {
    return { closed: undefined, events: [] };
  }
  const ended = finish(pending, 'interrupted', deps.now());
  if (hasEndedAttempt(deps.readAll(), pending.attemptId)) {
    /*
     * 이미 로그에 종료가 있다. checkpoint 삭제가 조용히 무시된 경우다. 한 시도에 종료가
     * 두 번 붙으면 서버가 `attempt_multiple_end`로 완료를 막는다.
     */
    deps.save(ended.state);
    return { closed: undefined, events: [] };
  }
  for (const event of ended.events) {
    deps.record(event.name, event.payload);
  }
  deps.save(ended.state);
  return { closed: pending.attemptId, events: ended.events.map((event) => event.name) };
}
